import type {
  CheckTypeConfig,
  CheckTypeKey,
  CheckTypeLimit,
  PaymentPeriod,
  Pricelist,
  PricelistCheckTypes,
  PricelistStatus,
  PriceItem,
} from "../../api/pricelist";
import { CHECK_TYPE_KEYS, CHECK_TYPE_LABELS } from "../../api/pricelist";

export interface PriceItemFormRow {
  period: PaymentPeriod;
  price: string;
}

export interface LimitFormRow {
  period: PaymentPeriod;
  count: string;
}

export interface CheckTypeFormValues {
  enabled: boolean;
  limits: LimitFormRow[];
  categoryIds: string[];
  schedule: boolean;
  report: boolean;
}

export interface PricelistFormValues {
  name: string;
  description: string;
  status: PricelistStatus;
  visibleForUsers: boolean;
  priceItemList: PriceItemFormRow[];
  checkTypes: Record<CheckTypeKey, CheckTypeFormValues>;
}

export const emptyLimitRow = (): LimitFormRow => ({
  period: "month",
  count: "",
});

export const emptyCheckTypeForm = (): CheckTypeFormValues => ({
  enabled: false,
  limits: [emptyLimitRow()],
  categoryIds: [],
  schedule: false,
  report: false,
});

export const emptyPricelistForm = (): PricelistFormValues => ({
  name: "",
  description: "",
  status: "active",
  visibleForUsers: true,
  priceItemList: [{ period: "month", price: "" }],
  checkTypes: {
    siteCheck: emptyCheckTypeForm(),
    policyCheck: emptyCheckTypeForm(),
    it: emptyCheckTypeForm(),
  },
});

const normalizePaymentPeriod = (period: string): PaymentPeriod => {
  if (period === "month" || period === "quarter" || period === "year") return period;
  return "month";
};

const isLimitFilled = (row: LimitFormRow): boolean => Boolean(row.count.trim());

const readLimitCount = (item: {
  count?: number;
  limit?: number;
}): number | null => {
  const count = Number(item.count);
  if (Number.isFinite(count) && count >= 0) return count;
  const legacy = Number(item.limit);
  if (Number.isFinite(legacy) && legacy >= 0) return legacy;
  return null;
};

export const resolveCheckTypeLimits = (
  config?: CheckTypeConfig | null
): CheckTypeLimit[] => {
  if (!config) return [];

  if (Array.isArray(config.limits) && config.limits.length > 0) {
    return config.limits.flatMap((item) => {
      const count = readLimitCount(item as CheckTypeLimit & { limit?: number });
      if (count == null || count <= 0) return [];
      return [{ period: normalizePaymentPeriod(String(item.period)), count }];
    });
  }

  return [];
};

export const getPrimaryCheckTypeLimit = (limits: CheckTypeLimit[]): number => {
  if (!limits.length) return 0;
  const month = limits.find((item) => item.period === "month");
  if (month) return month.count;
  return Math.max(...limits.map((item) => item.count));
};

const configToForm = (config?: CheckTypeConfig): CheckTypeFormValues => {
  const limits = resolveCheckTypeLimits(config);
  return {
    enabled: config?.enabled === true,
    limits: limits.length
      ? limits.map((item) => ({
          period: item.period,
          count: String(item.count),
        }))
      : [emptyLimitRow()],
    categoryIds: (config?.categoryIds ?? []).filter((id): id is string => Boolean(id?.trim())),
    schedule: config?.options?.schedule === true,
    report: config?.options?.report === true,
  };
};

const legacyToCheckTypes = (pricelist: Pricelist): Record<CheckTypeKey, CheckTypeFormValues> => {
  const base = emptyPricelistForm().checkTypes;
  const categoryIds = [
    ...new Set(
      (pricelist.tariffItemList ?? [])
        .map((item) => item.categoryId?.trim())
        .filter((id): id is string => Boolean(id))
    ),
  ];

  const siteLimitsByPeriod = new Map<PaymentPeriod, number>();
  for (const item of pricelist.tariffItemList ?? []) {
    const limit = Number(item.limit);
    if (!Number.isFinite(limit) || limit <= 0) continue;
    const period = normalizePaymentPeriod(String(item.period));
    siteLimitsByPeriod.set(period, Math.max(siteLimitsByPeriod.get(period) ?? 0, limit));
  }

  if (categoryIds.length || siteLimitsByPeriod.size > 0 || (pricelist.siteAmount ?? 0) > 0) {
    const limits =
      siteLimitsByPeriod.size > 0
        ? [...siteLimitsByPeriod.entries()].map(([period, count]) => ({
            period,
            count: String(count),
          }))
        : [
            {
              period: "month" as PaymentPeriod,
              count: String(Math.max(pricelist.siteAmount ?? 0, 1)),
            },
          ];

    base.siteCheck = {
      enabled: true,
      limits,
      categoryIds,
      schedule: pricelist.scanByScheduleEnabled === true,
      report: pricelist.emailNotification === true,
    };
  }

  if (pricelist.policyCheckEnabled === true) {
    const usage = pricelist.policyCheckSettings?.usageSettings ?? [];
    base.policyCheck = {
      enabled: true,
      limits: usage.length
        ? usage.map((item) => ({
            period: normalizePaymentPeriod(String(item.period)),
            count: String(item.limit ?? ""),
          }))
        : [emptyLimitRow()],
      categoryIds: [],
      schedule: false,
      report: pricelist.emailNotification === true,
    };
  }

  return base;
};

const resolvePriceRows = (pricelist: Pricelist): PriceItemFormRow[] => {
  const fromPrice = pricelist.price?.periods;
  const fromList = pricelist.priceItemList;
  const source = fromPrice?.length ? fromPrice : fromList;

  if (source?.length) {
    return source.map((item) => ({
      period: normalizePaymentPeriod(String(item.period)),
      price: String(item.price ?? ""),
    }));
  }

  return [{ period: "month", price: "" }];
};

export const pricelistToForm = (pricelist: Pricelist): PricelistFormValues => {
  const hasCheckTypes =
    pricelist.checkTypes != null &&
    CHECK_TYPE_KEYS.some((key) => pricelist.checkTypes?.[key] != null);

  const checkTypes = hasCheckTypes
    ? {
        siteCheck: configToForm(pricelist.checkTypes?.siteCheck),
        policyCheck: configToForm(pricelist.checkTypes?.policyCheck),
        it: configToForm(pricelist.checkTypes?.it),
      }
    : legacyToCheckTypes(pricelist);

  return {
    name: pricelist.name ?? "",
    description: pricelist.description ?? "",
    status: pricelist.status ?? "active",
    visibleForUsers: pricelist.visibleForUsers !== false,
    priceItemList: resolvePriceRows(pricelist),
    checkTypes,
  };
};

const formCheckTypeToConfig = (form: CheckTypeFormValues): CheckTypeConfig => ({
  enabled: form.enabled,
  categoryIds: form.categoryIds,
  limits: form.limits.filter(isLimitFilled).map((row) => ({
    period: row.period,
    count: Number(row.count),
  })),
  options: {
    schedule: form.schedule,
    report: form.report,
  },
});

export const formToPricelist = (form: PricelistFormValues): Pricelist => {
  const periods: PriceItem[] = form.priceItemList
    .filter((item) => item.price.trim())
    .map((item) => ({
      period: item.period,
      price: Number(item.price),
    }));

  const checkTypes = {
    siteCheck: formCheckTypeToConfig(form.checkTypes.siteCheck),
    policyCheck: formCheckTypeToConfig(form.checkTypes.policyCheck),
    it: formCheckTypeToConfig(form.checkTypes.it),
  } satisfies PricelistCheckTypes;

  const availablePeriods = [...new Set(periods.map((item) => item.period))] as PaymentPeriod[];

  return {
    name: form.name.trim(),
    description: form.description.trim() || undefined,
    status: form.status,
    visibleForUsers: form.visibleForUsers,
    checkTypes,
    price: {
      periods,
      availablePeriods,
    },
  };
};

export const validatePricelistForm = (form: PricelistFormValues): string | null => {
  if (!form.name.trim()) {
    return "Укажите название тарифа";
  }

  const hasValidPrice = form.priceItemList.some((item) => {
    const price = Number(item.price);
    return item.price.trim() && Number.isFinite(price) && price >= 0;
  });
  if (!hasValidPrice) return "Добавьте хотя бы одну стоимость";

  for (const item of form.priceItemList) {
    if (!item.price.trim()) continue;
    const price = Number(item.price);
    if (!Number.isFinite(price) || price < 0) {
      return "Стоимость должна быть неотрицательным числом";
    }
  }

  const periods = form.priceItemList.map((item) => item.period);
  if (new Set(periods).size !== periods.length) {
    return "Периоды оплаты не должны повторяться";
  }

  const enabledTypes = CHECK_TYPE_KEYS.filter((key) => form.checkTypes[key].enabled);
  if (!enabledTypes.length) {
    return "Включите хотя бы один тип проверки";
  }

  for (const key of enabledTypes) {
    const typeForm = form.checkTypes[key];
    const filledLimits = typeForm.limits.filter(isLimitFilled);
    if (!filledLimits.length) {
      return `Добавьте хотя бы один лимит для «${CHECK_TYPE_LABELS[key]}»`;
    }

    const limitPeriods = filledLimits.map((row) => row.period);
    if (new Set(limitPeriods).size !== limitPeriods.length) {
      return `Периоды лимитов для «${CHECK_TYPE_LABELS[key]}» не должны повторяться`;
    }

    for (const row of filledLimits) {
      const count = Number(row.count);
      if (!Number.isFinite(count) || count <= 0 || !Number.isInteger(count)) {
        return `Лимит для «${CHECK_TYPE_LABELS[key]}» должен быть целым числом > 0`;
      }
    }

    if (!typeForm.categoryIds.length) {
      return `Выберите хотя бы одну категорию для «${CHECK_TYPE_LABELS[key]}»`;
    }
  }

  return null;
};
